/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosPromise } from 'axios';
import proxy from './proxy';
import { DatalinkAPIModels } from '../generated/src/dlAPI';
import {
  GetPlatformMeshesResponse,
  GetMeshBoundariesResponse,
  MeshStatus,
  GetMeshTilegridResponse,
} from '../generated/src/models';

let apiUrl = '';

const init = () => {
  apiUrl = proxy.endpoints.apiUrl;
};

export type MeshFiles = GetPlatformMeshesResponse['_response']['parsedBody'];
// A `string` that becomes `TopoJSON.GeometryCollection` when parsed:
export type MeshBoundaries = GetMeshBoundariesResponse['_response']['parsedBody'];
export type MeshTileGridConfig = GetMeshTilegridResponse['_response']['parsedBody'];

export default class Api {
  static getMeshBoundaries(projectGuid: string, meshName: string): AxiosPromise<MeshBoundaries> {
    if (!apiUrl){
      init();
    }
    return proxy.http.get(
      apiUrl +
        '/api/Mesh/GetMeshBoundaries?projectGuid=' +
        projectGuid +
        '&meshName=' +
        meshName +
        '&geojson=' +
        true
    );
  }

  static getExtractionBuilder(
    options: DatalinkAPIModels.DatalinkAPIGetExtractionBuilderDataOptionalParams
  ): AxiosPromise<DatalinkAPIModels.ExtractionBuilderData> {
    if (!apiUrl){
      init();
    }
    return proxy.http.get(
      apiUrl +
        '/api/ExtractionBuilder/GetExtractionBuilderData?projectGuid=' +
        options.projectGuid +
        '&meshName=' +
        options.meshName +
        '&modelType=' +
        options.modelType
    );
  }

  static getExtractionBuilderJobs(
    options: DatalinkAPIModels.DatalinkAPIJobsOptionalParams
  ): AxiosPromise<DatalinkAPIModels.JobsResponse> {
        if (!apiUrl){
      init();
    }
    return proxy.http.post(apiUrl + '/api/ExtractionBuilder/Jobs', options);
  }

  static getProviderXyCoords(
    options: DatalinkAPIModels.DatalinkAPIGetProviderXyCoordsOptionalParams
  ): AxiosPromise<DatalinkAPIModels.GetProviderXyCoordsResponse> {
        if (!apiUrl){
      init();
    }
    return proxy.http.get(
      apiUrl +
        '/api/ExtractionBuilder/GetProviderXyCoords?projectGuid=' +
        options.projectGuid +
        '&datasetGuid=' +
        options.datasetGuid +
        '&meshName=' +
        options.meshName +
        '&modelType=' +
        options.modelType
    );
  }

  static uploadRequest(projectGuid: string, meshName: string) {
    if (!apiUrl){
      init();
    }
    return proxy.http.get(
      apiUrl + '/api/Mesh/UploadRequest?projectGuid=' + projectGuid + '&meshName=' + meshName
    );
  }

  static uploadCompleted(projectGuid: string, meshName: string) {
    if (!apiUrl){
      init();
    }
    return proxy.http.post(
      apiUrl + '/api/Mesh/UploadCompleted?projectGuid=' + projectGuid + '&meshName=' + meshName
    );
  }

  static getTileGridConfig(
    projectGuid: string,
    meshName: string,
    reproject: boolean
  ): AxiosPromise<MeshTileGridConfig> {
        if (!apiUrl){
      init();
    }
    return proxy.http.get(
      apiUrl +
        '/api/Mesh/Tiles/Tilegrid?projectGuid=' +
        projectGuid +
        '&meshName=' +
        meshName +
        '&reproject=' +
        reproject
    );
  }

  static async meshTileLoadGet(_tile: string, url: string) {
    if (!apiUrl){
      init();
    }
    return await proxy.http.get(url);
  }

  static listMeshStatus(projectGuid: string): AxiosPromise<MeshStatus[]> {
        if (!apiUrl){
      init();
    }
    return proxy.http.get(apiUrl + '/api/Mesh/ListMeshStatus?projectGuid=' + projectGuid);
  }

  // static startExtraction(projectGuid, Params) {
  static startExtraction(
    options: DatalinkAPIModels.DatalinkAPIStartExtractionOptionalParams,
    extra
  ): AxiosPromise<DatalinkAPIModels.DatalinkAPIStartExtractionOptionalParams> {
        if (!apiUrl){
      init();
    }
    return proxy.http.post(
      apiUrl +
        '/api/ExtractionBuilder/StartExtraction?projectGuid=' +
        options.projectGuid +
        '&destinationProjectGuid=' +
        options.destinationProjectGuid +
        '&meshName=' +
        options.meshName +
        '&modelType=' +
        options.modelType +
        '&startDate=' +
        options.startDate +
        '&endDate=' +
        options.endDate +
        '&initConditionsDate=' +
        options.initConditionsDate +
        '&saveForcingsInNativeGridResolution=' +
        options.saveForcingsInNativeGridResolution +
        '&extrapolateSpatialGaps=' +
        options.extrapolateSpatialGaps +
        '&seaLevelAdjustment=' +
        options.seaLevelAdjustment,
      extra,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  static cancelExtraction(
    jobGuid: string
  ): AxiosPromise<DatalinkAPIModels.DatalinkAPICancelExtractionOptionalParams> {
        if (!apiUrl){
      init();
    }
    return proxy.http.post(apiUrl + '/api/ExtractionBuilder/CancelExtraction?jobGuid=' + jobGuid, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static listExtractionStatus(projectGuid: string) {
        if (!apiUrl){
      init();
    }
    return proxy.http.get(apiUrl + '/api/Mesh/ListExtractionStatus?projectGuid=' + projectGuid);
  }

  static getPlatformMeshes(projectGuid: string): AxiosPromise<MeshFiles> {
        if (!apiUrl){
      init();
    }
    return proxy.http.get(apiUrl + '/api/Mesh/GetPlatformMeshes?projectGuid=' + projectGuid);
  }

  static importPlatformMeshes({
    projectGuid,
    sourceProjectGuid,
    meshNames,
  }: {
    projectGuid: string;
    sourceProjectGuid: string;
    meshNames: MeshFiles;
  }): AxiosPromise<MeshFiles> {
        if (!apiUrl){
      init();
    }
    return proxy.http.post<MeshFiles>(
      apiUrl +
        '/api/Mesh/ImportPlatformMeshes?projectGuid=' +
        projectGuid +
        '&sourceProjectGuid=' +
        sourceProjectGuid,
      meshNames
    );
  }

  // This 4 endpoints could and should be moved to projectApi, but there is a class conflict when importing
  static async getProject(projectGUID: string) {
        if (!apiUrl){
      init();
    }
    return await proxy.http.get(apiUrl + '/api/Project/GetProject?projectId=' + projectGUID);
  }

  static async getProjectPath(projectGUID: string) {
        if (!apiUrl){
      init();
    }
    return await proxy.http.get(apiUrl + '/api/Project/GetProjectPath?projectId=' + projectGUID);
  }

  static async getSubProjects(projectGUID: string) {
        if (!apiUrl){
      init();
    }
    return await proxy.http.get(apiUrl + '/api/Project/GetSubProjects?projectId=' + projectGUID);
  }

  static async getProjectDatasets(projectGUID: string) {
        if (!apiUrl){
      init();
    }
    return await proxy.http.get(apiUrl + '/api/Project/GetDatasets?projectId=' + projectGUID);
  }

  static deleteMesh(projectGuid: string, meshfiles: any) {
        if (!apiUrl){
      init();
    }
    return proxy.http.post(apiUrl + '/api/Mesh/DeleteMeshes?projectGuid=' + projectGuid, meshfiles);
  }
}
